'use client'

import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  LoadingState,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Spinner,
  Switch,
  Textarea,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@repo/ui'
import { cn } from '@repo/ui'
import {
  AlertTriangle,
  Check,
  ChevronDown,
  ChevronUp,
  FlaskConical,
  Pencil,
  Play,
  Plus,
  Sparkles,
  Trash2,
  X,
} from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

import {
  type AgentTestCaseResult,
  type AgentTestRun,
  type AgentTestScenario,
  type ScenarioInput,
  type SuggestedScenario,
  type TestCriterion,
  useCancelTestRun,
  useCreateScenario,
  useDeleteScenario,
  useStartTestRun,
  useSuggestScenarios,
  useTestQuota,
  useTestRun,
  useTestRuns,
  useTestScenarios,
  useUpdateScenario,
} from '@/hooks/use-agent-tests'

interface TestsTabProps {
  orgId: string
  agentId: string
}

const TOOL_LABELS: Record<string, string> = {
  'lead.add_tag': 'Adicionar tag',
  'lead.add_note': 'Adicionar nota',
  'lead.save_field': 'Salvar campo do lead',
  'funnel.move_stage': 'Mover etapa do funil',
  'qualification.submit_evidence': 'Marcar hot lead',
  'schedule.check_availability': 'Consultar agenda',
  'schedule.book': 'Agendar reunião',
  'schedule.reschedule': 'Remarcar reunião',
  'schedule.cancel': 'Cancelar agendamento',
  'cadence.enroll': 'Inscrever em cadência',
  'attendance.transfer_to_human': 'Transferir para humano',
  'mcp.call_tool': 'Chamar ferramenta MCP',
}

const RUN_STATUS_LABELS: Record<AgentTestRun['status'], string> = {
  PENDING: 'Na fila',
  RUNNING: 'Executando',
  COMPLETED: 'Concluída',
  FAILED: 'Falhou',
  CANCELLED: 'Cancelada',
}

const CASE_STATUS_LABELS: Record<AgentTestCaseResult['status'], string> = {
  PENDING: 'Na fila',
  RUNNING: 'Executando',
  PASSED: 'Passou',
  FAILED: 'Falhou',
  ERROR: 'Erro',
  SKIPPED: 'Pulado',
}

const STOP_REASON_LABELS: Record<string, string> = {
  MAX_TURNS: 'Limite de turnos',
  SIMULATOR_ENDED: 'Lead encerrou',
  TRANSFERRED: 'Transferiu para humano',
  BOOKED: 'Agendou reunião',
  AGENT_ERROR: 'Erro do agente',
}

function apiErrorMessage(err: unknown): string | null {
  const message = (err as { response?: { data?: { message?: unknown } } })
    ?.response?.data?.message
  return typeof message === 'string' ? message : null
}

function criterionLabel(criterion: TestCriterion): string {
  if (criterion.type === 'LLM_JUDGE') return criterion.text
  const toolLabel = TOOL_LABELS[criterion.toolId] ?? criterion.toolId
  return criterion.type === 'ACTION_EXPECTED'
    ? `Deve executar: ${toolLabel}`
    : `Não pode executar: ${toolLabel}`
}

function formatCost(costUsd: string | number): string {
  const value = Number(costUsd)
  if (!Number.isFinite(value) || value === 0) return '—'
  return `US$ ${value.toFixed(value < 0.01 ? 4 : 2)}`
}

// ─── Editor de cenário ─────────────────────────────────────────────────────

type ScenarioFormValue = {
  name: string
  persona: string
  objective: string
  maxTurns: number
  criteria: TestCriterion[]
}

const EMPTY_FORM: ScenarioFormValue = {
  name: '',
  persona: '',
  objective: '',
  maxTurns: 8,
  criteria: [{ type: 'LLM_JUDGE', text: '' }],
}

function ScenarioFormDialog({
  open,
  initial,
  title,
  saving,
  onClose,
  onSave,
}: {
  open: boolean
  initial: ScenarioFormValue
  title: string
  saving: boolean
  onClose: () => void
  onSave: (value: ScenarioFormValue) => void
}) {
  const [form, setForm] = useState<ScenarioFormValue>(initial)

  // Reinicializa o form quando o dialog abre com outro cenário
  const [initialKey, setInitialKey] = useState(initial)
  if (initial !== initialKey) {
    setInitialKey(initial)
    setForm(initial)
  }

  const updateCriterion = (index: number, criterion: TestCriterion) => {
    setForm((prev) => ({
      ...prev,
      criteria: prev.criteria.map((item, i) =>
        i === index ? criterion : item
      ),
    }))
  }

  const valid =
    form.name.trim().length > 0 &&
    form.persona.trim().length >= 10 &&
    form.criteria.length > 0 &&
    form.criteria.every((criterion) =>
      criterion.type === 'LLM_JUDGE'
        ? criterion.text.trim().length >= 3
        : !!criterion.toolId
    )

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label>Nome do cenário</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              placeholder="Ex.: Lead cético que pergunta preço"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>Persona do lead simulado</Label>
            <Textarea
              value={form.persona}
              onChange={(e) =>
                setForm((p) => ({ ...p, persona: e.target.value }))
              }
              rows={4}
              placeholder="Quem é o lead, contexto, tom e dados que possui. Ex.: Produtor de soja de 500ha em MT, desconfiado de consultorias, já teve experiência ruim..."
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>Objetivo do lead (opcional)</Label>
            <Input
              value={form.objective}
              onChange={(e) =>
                setForm((p) => ({ ...p, objective: e.target.value }))
              }
              placeholder="Ex.: Descobrir o preço antes de dar qualquer dado"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>Máximo de turnos: {form.maxTurns}</Label>
            <input
              type="range"
              min={2}
              max={12}
              value={form.maxTurns}
              onChange={(e) =>
                setForm((p) => ({ ...p, maxTurns: Number(e.target.value) }))
              }
            />
          </div>

          <div className="flex flex-col gap-2">
            <Label>Critérios de sucesso</Label>
            {form.criteria.map((criterion, index) => (
              <div key={index} className="flex items-start gap-2">
                <Select
                  value={criterion.type}
                  onValueChange={(type) =>
                    updateCriterion(
                      index,
                      type === 'LLM_JUDGE'
                        ? { type: 'LLM_JUDGE', text: '' }
                        : {
                            type: type as
                              | 'ACTION_EXPECTED'
                              | 'ACTION_FORBIDDEN',
                            toolId: 'attendance.transfer_to_human',
                          }
                    )
                  }
                >
                  <SelectTrigger className="w-[150px] shrink-0">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="LLM_JUDGE">Comportamento</SelectItem>
                    <SelectItem value="ACTION_EXPECTED">
                      Ação esperada
                    </SelectItem>
                    <SelectItem value="ACTION_FORBIDDEN">
                      Ação proibida
                    </SelectItem>
                  </SelectContent>
                </Select>
                {criterion.type === 'LLM_JUDGE' ? (
                  <Textarea
                    value={criterion.text}
                    onChange={(e) =>
                      updateCriterion(index, {
                        type: 'LLM_JUDGE',
                        text: e.target.value,
                      })
                    }
                    rows={1}
                    className="min-h-9 flex-1"
                    placeholder="Ex.: O agente não deve inventar preços"
                  />
                ) : (
                  <Select
                    value={criterion.toolId}
                    onValueChange={(toolId) =>
                      updateCriterion(index, { type: criterion.type, toolId })
                    }
                  >
                    <SelectTrigger className="flex-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(TOOL_LABELS).map(([toolId, label]) => (
                        <SelectItem key={toolId} value={toolId}>
                          {label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="shrink-0"
                  disabled={form.criteria.length === 1}
                  onClick={() =>
                    setForm((p) => ({
                      ...p,
                      criteria: p.criteria.filter((_, i) => i !== index),
                    }))
                  }
                >
                  <X className="size-4" />
                </Button>
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="self-start"
              disabled={form.criteria.length >= 12}
              onClick={() =>
                setForm((p) => ({
                  ...p,
                  criteria: [...p.criteria, { type: 'LLM_JUDGE', text: '' }],
                }))
              }
            >
              <Plus className="size-3.5" />
              Adicionar critério
            </Button>
          </div>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button
              type="button"
              disabled={!valid || saving}
              onClick={() => onSave(form)}
            >
              {saving && <Spinner />}
              Salvar cenário
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

// ─── Detalhe da run ────────────────────────────────────────────────────────

function CaseResultCard({ result }: { result: AgentTestCaseResult }) {
  const [showTranscript, setShowTranscript] = useState(false)

  const statusColor =
    result.status === 'PASSED'
      ? 'bg-success/15 text-success'
      : result.status === 'FAILED' || result.status === 'ERROR'
        ? 'bg-destructive/15 text-destructive'
        : 'bg-muted text-muted-foreground'

  return (
    <div className="border-border bg-card rounded-lg border p-3">
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <span className={cn('rounded px-2 py-0.5 text-xs', statusColor)}>
            {CASE_STATUS_LABELS[result.status]}
          </span>
          <span className="truncate text-sm font-medium">
            {result.scenarioName}
          </span>
        </div>
        <span className="text-muted-foreground shrink-0 text-xs">
          {result.stopReason
            ? (STOP_REASON_LABELS[result.stopReason] ?? result.stopReason)
            : ''}
        </span>
      </div>

      {result.errorMessage && (
        <p className="text-destructive mt-2 text-xs">{result.errorMessage}</p>
      )}

      {result.verdicts.length > 0 && (
        <div className="mt-2 flex flex-col gap-1.5">
          {result.verdicts.map((verdict) => (
            <div key={verdict.criterionIndex} className="flex gap-2 text-xs">
              {verdict.passed ? (
                <Check className="text-success mt-0.5 size-3.5 shrink-0" />
              ) : (
                <X className="text-destructive mt-0.5 size-3.5 shrink-0" />
              )}
              <div className="min-w-0">
                <p className="font-medium">
                  {verdict.type === 'LLM_JUDGE'
                    ? verdict.criterion
                    : criterionLabel({
                        type: verdict.type,
                        toolId: verdict.criterion,
                      } as TestCriterion)}
                </p>
                <p className="text-muted-foreground">{verdict.reasoning}</p>
                {verdict.evidence && (
                  <p className="text-muted-foreground mt-0.5 italic">
                    “{verdict.evidence}”
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {result.judgeNotes && (
        <p className="text-muted-foreground bg-muted/50 mt-2 rounded-md p-2 text-xs">
          {result.judgeNotes}
        </p>
      )}

      {result.transcript.length > 0 && (
        <div className="mt-2">
          <button
            type="button"
            onClick={() => setShowTranscript((p) => !p)}
            className="text-muted-foreground hover:text-foreground flex items-center gap-1 text-xs transition-colors"
          >
            {showTranscript ? (
              <ChevronUp className="size-3" />
            ) : (
              <ChevronDown className="size-3" />
            )}
            Transcrição ({result.turns}{' '}
            {result.turns === 1 ? 'turno' : 'turnos'})
          </button>
          {showTranscript && (
            <div className="mt-2 flex flex-col gap-1.5">
              {result.transcript.map((entry, index) => (
                <div
                  key={index}
                  className={cn(
                    'max-w-[85%] rounded-lg px-3 py-2 text-xs',
                    entry.role === 'lead'
                      ? 'bg-muted self-start'
                      : 'bg-primary/10 self-end'
                  )}
                >
                  <p className="text-muted-foreground text-2xs mb-0.5 font-medium uppercase">
                    {entry.role === 'lead' ? 'Lead (simulado)' : 'Agente'}
                  </p>
                  <p className="whitespace-pre-wrap">{entry.content}</p>
                  {!!entry.actions?.length && (
                    <p className="text-muted-foreground text-2xs mt-1">
                      ⚙️{' '}
                      {entry.actions
                        .map((action) => String(action.type))
                        .join(', ')}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function RunDetail({
  orgId,
  agentId,
  runId,
}: {
  orgId: string
  agentId: string
  runId: string
}) {
  const { data: run, isLoading } = useTestRun(orgId, agentId, runId)

  if (isLoading || !run) {
    return (
      <div className="text-muted-foreground flex items-center gap-2 p-3 text-sm">
        <Spinner /> Carregando resultados...
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-2 pt-2">
      {(run.results ?? []).map((result) => (
        <CaseResultCard key={result.id} result={result} />
      ))}
    </div>
  )
}

function RunCard({
  run,
  orgId,
  agentId,
  onCancel,
  cancelling,
}: {
  run: AgentTestRun
  orgId: string
  agentId: string
  onCancel: (runId: string) => void
  cancelling: boolean
}) {
  const [expanded, setExpanded] = useState(false)
  const isActive = run.status === 'PENDING' || run.status === 'RUNNING'

  const statusColor =
    run.status === 'COMPLETED'
      ? 'bg-success/15 text-success'
      : run.status === 'FAILED'
        ? 'bg-destructive/15 text-destructive'
        : run.status === 'RUNNING'
          ? 'bg-info/15 text-info'
          : 'bg-muted text-muted-foreground'

  return (
    <div className="border-border bg-card rounded-lg border p-3">
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          {isActive && <Spinner className="size-3.5 shrink-0" />}
          <span className={cn('rounded px-2 py-0.5 text-xs', statusColor)}>
            {RUN_STATUS_LABELS[run.status]}
          </span>
          <span className="text-muted-foreground text-xs">
            {new Date(run.createdAt).toLocaleString('pt-BR', {
              day: '2-digit',
              month: '2-digit',
              hour: '2-digit',
              minute: '2-digit',
            })}
          </span>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="text-muted-foreground text-2xs font-mono">
                v:{run.instructionsHash.slice(0, 8)}
              </span>
            </TooltipTrigger>
            <TooltipContent>
              Identificador do prompt testado
              {run.promptVersionId ? ' (versão do otimizador aplicada)' : ''}
            </TooltipContent>
          </Tooltip>
        </div>
        <div className="flex shrink-0 items-center gap-3 text-xs">
          <span>
            {run.completedScenarios}/{run.totalScenarios}
          </span>
          {run.status !== 'PENDING' && (
            <span>
              <span className="text-success">{run.passedScenarios} ✓</span>{' '}
              <span className="text-destructive">{run.failedScenarios} ✗</span>
            </span>
          )}
          <span className="text-muted-foreground">
            {formatCost(run.costUsd)}
          </span>
          {isActive ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={cancelling}
              onClick={() => onCancel(run.id)}
            >
              Cancelar
            </Button>
          ) : (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setExpanded((p) => !p)}
            >
              {expanded ? (
                <ChevronUp className="size-4" />
              ) : (
                <ChevronDown className="size-4" />
              )}
            </Button>
          )}
        </div>
      </div>
      {run.errorMessage && (
        <p className="text-destructive mt-1 text-xs">{run.errorMessage}</p>
      )}
      {expanded && <RunDetail orgId={orgId} agentId={agentId} runId={run.id} />}
    </div>
  )
}

// ─── Aba principal ─────────────────────────────────────────────────────────

export function TestsTab({ orgId, agentId }: TestsTabProps) {
  const { data: scenarios = [], isLoading: scenariosLoading } =
    useTestScenarios(orgId, agentId)
  const { data: quota } = useTestQuota(orgId, agentId)
  const { data: runs = [] } = useTestRuns(orgId, agentId)

  const createScenario = useCreateScenario(orgId, agentId)
  const updateScenario = useUpdateScenario(orgId, agentId)
  const deleteScenario = useDeleteScenario(orgId, agentId)
  const suggestScenarios = useSuggestScenarios(orgId, agentId)
  const startRun = useStartTestRun(orgId, agentId)
  const cancelRun = useCancelTestRun(orgId, agentId)

  const [formOpen, setFormOpen] = useState(false)
  const [formInitial, setFormInitial] = useState<ScenarioFormValue>(EMPTY_FORM)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [suggestions, setSuggestions] = useState<SuggestedScenario[]>([])

  const openCreate = () => {
    setEditingId(null)
    setFormInitial({
      ...EMPTY_FORM,
      criteria: [{ type: 'LLM_JUDGE', text: '' }],
    })
    setFormOpen(true)
  }

  const openEdit = (scenario: AgentTestScenario) => {
    setEditingId(scenario.id)
    setFormInitial({
      name: scenario.name,
      persona: scenario.persona,
      objective: scenario.objective ?? '',
      maxTurns: scenario.maxTurns,
      criteria: scenario.criteria,
    })
    setFormOpen(true)
  }

  const handleSave = async (value: ScenarioFormValue) => {
    const body: ScenarioInput = {
      name: value.name.trim(),
      persona: value.persona.trim(),
      objective: value.objective.trim() || null,
      maxTurns: value.maxTurns,
      criteria: value.criteria,
    }
    try {
      if (editingId) {
        await updateScenario.mutateAsync({ scenarioId: editingId, ...body })
      } else {
        await createScenario.mutateAsync(body)
      }
      setFormOpen(false)
      toast.success(editingId ? 'Cenário atualizado' : 'Cenário criado')
    } catch (err) {
      toast.error(apiErrorMessage(err) ?? 'Não foi possível salvar o cenário')
    }
  }

  const handleSuggest = async () => {
    try {
      const result = await suggestScenarios.mutateAsync({ count: 5 })
      setSuggestions(result)
      if (result.length === 0) toast.info('Nenhuma sugestão gerada')
    } catch (err) {
      toast.error(apiErrorMessage(err) ?? 'Não foi possível gerar sugestões')
    }
  }

  const handleSaveSuggestion = async (suggestion: SuggestedScenario) => {
    try {
      await createScenario.mutateAsync({
        ...suggestion,
        source: 'AI_SUGGESTED',
      })
      setSuggestions((prev) => prev.filter((item) => item !== suggestion))
      toast.success('Cenário salvo')
    } catch (err) {
      toast.error(apiErrorMessage(err) ?? 'Não foi possível salvar o cenário')
    }
  }

  const handleStartRun = async () => {
    try {
      await startRun.mutateAsync()
      toast.success('Suíte de testes enfileirada')
    } catch (err) {
      toast.error(apiErrorMessage(err) ?? 'Não foi possível iniciar a execução')
    }
  }

  const handleCancelRun = async (runId: string) => {
    try {
      await cancelRun.mutateAsync(runId)
      toast.success('Execução cancelada')
    } catch (err) {
      toast.error(apiErrorMessage(err) ?? 'Não foi possível cancelar')
    }
  }

  return (
    <div className="flex flex-col gap-8">
      {/* Header */}
      <section className="bg-card rounded-md border p-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="flex items-center gap-2 text-lg font-semibold">
              <FlaskConical className="size-5" />
              Testes automatizados
            </h2>
            <p className="text-muted-foreground mt-1 text-sm">
              Um lead simulado por IA conversa com seu agente (mesmo motor do
              preview) e um juiz avalia cada conversa contra os critérios dos
              cenários. Nada é enviado ao WhatsApp nem salvo em contatos reais.
            </p>
          </div>
          <Tooltip>
            <TooltipTrigger asChild>
              <span>
                <Button
                  type="button"
                  disabled={!quota?.canRun || startRun.isPending}
                  onClick={() => void handleStartRun()}
                >
                  {startRun.isPending ? (
                    <Spinner />
                  ) : (
                    <Play className="size-4" />
                  )}
                  Rodar suíte ({quota?.enabledScenarios ?? 0})
                </Button>
              </span>
            </TooltipTrigger>
            {quota?.blockedReason && (
              <TooltipContent>{quota.blockedReason}</TooltipContent>
            )}
          </Tooltip>
        </div>
      </section>

      <div className="text-muted-foreground bg-card flex items-start gap-2 rounded-md border p-3 text-xs">
        <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
        <span>
          Agendamentos e campos são simulados, mas ferramentas MCP vinculadas ao
          agente são executadas de verdade — igual ao preview manual.
          {quota
            ? ` Execuções este mês: ${quota.usedThisMonth}${quota.monthlyCap > 0 ? `/${quota.monthlyCap}` : ''}.`
            : ''}
        </span>
      </div>

      {/* Cenários */}
      <section className="bg-card flex flex-col gap-3 rounded-md border p-5">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold">Cenários</h3>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={suggestScenarios.isPending}
              onClick={() => void handleSuggest()}
            >
              {suggestScenarios.isPending ? (
                <Spinner className="size-3.5" />
              ) : (
                <Sparkles className="size-3.5" />
              )}
              Gerar com IA
            </Button>
            <Button type="button" size="sm" onClick={openCreate}>
              <Plus className="size-3.5" />
              Novo cenário
            </Button>
          </div>
        </div>

        {scenariosLoading ? (
          <LoadingState />
        ) : scenarios.length === 0 && suggestions.length === 0 ? (
          <p className="text-muted-foreground bg-card rounded-lg border border-dashed p-6 text-center text-sm">
            Nenhum cenário ainda. Crie um manualmente ou gere sugestões com IA a
            partir da configuração do agente.
          </p>
        ) : (
          <div className="flex flex-col gap-2">
            {scenarios.map((scenario) => (
              <div
                key={scenario.id}
                className={cn(
                  'border-border bg-card rounded-lg border p-3',
                  !scenario.enabled && 'opacity-60'
                )}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex min-w-0 items-center gap-2">
                    <span className="truncate text-sm font-medium">
                      {scenario.name}
                    </span>
                    {scenario.source === 'AI_SUGGESTED' && (
                      <Badge variant="secondary" className="text-2xs shrink-0">
                        IA
                      </Badge>
                    )}
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    <Switch
                      checked={scenario.enabled}
                      onCheckedChange={(enabled) =>
                        void updateScenario.mutateAsync({
                          scenarioId: scenario.id,
                          enabled,
                        })
                      }
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => openEdit(scenario)}
                    >
                      <Pencil className="size-3.5" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        if (confirm(`Remover o cenário "${scenario.name}"?`)) {
                          void deleteScenario.mutateAsync(scenario.id)
                        }
                      }}
                    >
                      <Trash2 className="size-3.5" />
                    </Button>
                  </div>
                </div>
                <p className="text-muted-foreground mt-1 line-clamp-1 text-xs">
                  {scenario.persona}
                </p>
              </div>
            ))}
          </div>
        )}

        {/* Sugestões pendentes */}
        {suggestions.length > 0 && (
          <div className="flex flex-col gap-2">
            <h4 className="text-muted-foreground text-xs font-semibold uppercase">
              Sugestões da IA — revise antes de salvar
            </h4>
            {suggestions.map((suggestion, index) => (
              <div
                key={index}
                className="border-primary/40 bg-card rounded-lg border border-dashed p-3"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate text-sm font-medium">
                    {suggestion.name}
                  </span>
                  <div className="flex shrink-0 gap-1">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      disabled={createScenario.isPending}
                      onClick={() => void handleSaveSuggestion(suggestion)}
                    >
                      <Check className="size-3.5" />
                      Salvar
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() =>
                        setSuggestions((prev) =>
                          prev.filter((item) => item !== suggestion)
                        )
                      }
                    >
                      <X className="size-3.5" />
                    </Button>
                  </div>
                </div>
                <p className="text-muted-foreground mt-1 text-xs">
                  {suggestion.persona}
                </p>
                {suggestion.objective && (
                  <p className="text-muted-foreground mt-1 text-xs">
                    <span className="font-medium">Objetivo:</span>{' '}
                    {suggestion.objective}
                  </p>
                )}
                <div className="mt-2 flex flex-wrap gap-1">
                  {suggestion.criteria.map((criterion, cIndex) => (
                    <span
                      key={cIndex}
                      className="bg-muted text-2xs rounded px-1.5 py-0.5"
                    >
                      {criterionLabel(criterion)}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Execuções */}
      <section className="bg-card flex flex-col gap-3 rounded-md border p-5">
        <h3 className="text-sm font-semibold">Execuções</h3>
        {runs.length === 0 ? (
          <p className="text-muted-foreground bg-card rounded-lg border border-dashed p-6 text-center text-sm">
            Nenhuma execução ainda.
          </p>
        ) : (
          <div className="flex flex-col gap-2">
            {runs.map((run) => (
              <RunCard
                key={run.id}
                run={run}
                orgId={orgId}
                agentId={agentId}
                onCancel={(runId) => void handleCancelRun(runId)}
                cancelling={cancelRun.isPending}
              />
            ))}
          </div>
        )}
      </section>

      <ScenarioFormDialog
        open={formOpen}
        initial={formInitial}
        title={editingId ? 'Editar cenário' : 'Novo cenário'}
        saving={createScenario.isPending || updateScenario.isPending}
        onClose={() => setFormOpen(false)}
        onSave={(value) => void handleSave(value)}
      />
    </div>
  )
}
